import type { ObjectId } from 'mongodb';

import { ApprovedVendorAllocation } from './AppSettings';
import { DirectPayMaterialVendorAliasOptions } from './DirectPayTypes';
import { MilestoneType } from './Milestones';
import { ProgramType } from './ProgramTypes';
import type { UserInfo, UserWithDate } from './UserTypes';

export enum OrganizationSettingsV2Types {
  milestoneClawbackSettings = 'milestoneClawbackSettings',
  materialClawbackSettings = 'materialClawbackSettings',
  directPaySettings = 'directPaySettings',
  paymentPlanSettings = 'paymentPlanSettings',
  underwritingSettings = 'underwritingSettings',
  invoiceSettings = 'invoiceSettings',
  safeHarborPricing = 'safeHarborPricing',
  orgEquipmentSettings = 'orgEquipmentSettings',
  reviewSlaSettings = 'reviewSlaSettings',
  maintenanceSettings = 'maintenanceSettings',
  accountBundlingSettings = 'accountBundlingSettings',
}

export type OrgV2AccountBundlingSettings = {
  enabled: boolean;
  setBy: UserWithDate;
};

// `source` is the org that holds the enabling setting; present only when `isInherited` is true.
export type OrgAccountBundlingResolution = {
  enabled: boolean;
  isInherited: boolean;
  source?: { alias: string; name: string };
};

// statuses for payment plan change workflow
export enum OrgV2PaymentPlanChangeStatus {
  proposed = 'proposed',
  accepted = 'accepted', // accepted by the organization
}

export type OrgV2PaymentPlanSettings = {
  paymentPlanId?: ObjectId;
  planToApplyToExistingAccounts?: ObjectId;
  changeStatus?: OrgV2PaymentPlanChangeStatus;
  setBy: UserWithDate;
  acceptedBy?: UserWithDate;
};

export type OrgV2ReviewSlaMilestoneHours = {
  submission?: number;
  resubmission?: number;
};

export type OrgV2ReviewSlaSettings = {
  enabled?: boolean;
  reviewPriorityTier?: number;
  slaBusinessHoursByMilestone?: Partial<Record<MilestoneType, OrgV2ReviewSlaMilestoneHours>>;
  setBy: UserWithDate;
};

export type OrgV2ReviewSlaSettingSource = {
  alias: string;
  name: string;
};

// The QC priority setting in effect for an org + program type.
// - `setting`: the org's effective setting — its own when `isInherited` is false, otherwise the
//   direct parent's effective setting.
// - `source`: the DIRECT parent (alias + name) this org inherits (or would inherit) from. Present
//   whenever the org has a parent, regardless of whether it is currently inheriting; absent for a
//   first parent.
// - `inheritedSetting`: the direct parent's effective setting, so the UI can always show what is (or
//   would be) inherited. Absent for a first parent.
export type OrgV2ReviewSlaSettingsResponse = {
  setting: OrganizationSettingsV2<OrgV2ReviewSlaSettings> | null;
  isInherited: boolean;
  source?: OrgV2ReviewSlaSettingSource;
  inheritedSetting?: OrganizationSettingsV2<OrgV2ReviewSlaSettings> | null;
};

export interface OrganizationDirectPayStateOverride {
  directPayMaterialVendorAlias: DirectPayMaterialVendorAliasOptions;
  directPayInstallerIdentifier: string;
  programName: string;
  subCompanyId: string;
  states: string[];
}

export interface OrgDirectPayAdditionalOptInVendor {
  directPayMaterialVendorAlias: DirectPayMaterialVendorAliasOptions;
  directPayInstallerIdentifier: string;
  programName: string;
  subCompanyId: string;
}

export interface OrganizationDirectPaySettingsV2 {
  directPayDefaultEnabled: boolean;
  directPayMaterialVendorAlias?: DirectPayMaterialVendorAliasOptions;
  directPayInstallerIdentifier?: string;
  programName?: string;
  directPayEnabledDate?: Date;
  stateOverrides?: OrganizationDirectPayStateOverride[];
  additionalOptInVendors?: OrgDirectPayAdditionalOptInVendor[];
  setBy?: UserInfo;
  programType?: ProgramType;
  subCompanyId: string;
}

// if you are going to load this type, make sure that you use findStateOptionalOrganizationSettingV2, as it has explicit support
// for conditional state overrides.
export interface OrganizationUnderwritingSettingsV2 {
  minFicoScore: number;
  ficoScoreErrorThreshold: number;
  creditExpiryTerm: number;
  yearsSinceLastBankruptcy: number;
  minFicoToRerun: number;
  maxAppsPerDay: number;
  autoQualify?: boolean;
  /**
   * Optional minimum FICO applied only when an application has more than one applicant.
   * When set, it fully replaces `minFicoScore` as the qualification threshold for that
   * application (even if lower), and only one applicant needs to meet it.
   */
  multipleApplicantMinimumFico?: number;
  /**
   * Credit optimization range (FIN-4635). When the first bureau's FICO falls within
   * [creditOptimizationMin, creditOptimizationMax] (inclusive), the waterfall pulls the next
   * bureau to seek a higher score — even if the first score already passes approval. Both bounds
   * must be set (and the LaunchDarkly flag `credit-waterfall-optimization` enabled) for the range
   * to take effect; an unset/partial range disables optimization. Separate from minFicoScore /
   * minFicoToRerun, which govern approval, not portfolio optimization.
   */
  creditOptimizationMin?: number;
  creditOptimizationMax?: number;
  state?: string;
  setBy?: UserWithDate;
}

export interface OrganizationInvoiceSettingsV2 {
  invoiceDueDays: number;
  installApprovalPauseDays?: number;
  materialsEstimatePauseDays?: number;
}

export enum OrganizationInvoiceSettingsV2Key {
  invoiceDueDays = 'invoiceDueDays',
  installApprovalPauseDays = 'installApprovalPauseDays',
  materialsEstimatePauseDays = 'materialsEstimatePauseDays',
}

export const OrganizationInvoiceSettingV2DisplayMap: Record<OrganizationInvoiceSettingsV2Key, string> = {
  [OrganizationInvoiceSettingsV2Key.invoiceDueDays]: 'Invoice Due Days',
  [OrganizationInvoiceSettingsV2Key.installApprovalPauseDays]: 'Install Approval Pause Days',
  [OrganizationInvoiceSettingsV2Key.materialsEstimatePauseDays]: 'Materials Estimate Pause Days',
};

// any additional properties like state, programType, etc. should be added to the value object, not to the base interface
export interface OrganizationSettingsV2<T extends Record<string, any> = Record<string, any>> {
  organizationId: string;
  inherit: boolean; // for explicit inheritance, lack of this doc for an org will be implicit inheritance
  effectiveDate: Date;
  type: OrganizationSettingsV2Types;
  programType?: ProgramType;
  value?: T;
}

export enum MilestoneClawbackSettingKey {
  ntpClawbackDays = 'ntpClawbackDays',
  installClawbackDays = 'installClawbackDays',
}

export enum MaterialClawbackSettingKey {
  partnerInstallSubmittal = 'partnerInstallSubmittal',
  partnerInstallApproval = 'partnerInstallApproval',
}

export const MilestoneClawbackSettingDisplayMap: Record<MilestoneClawbackSettingKey, string> = {
  [MilestoneClawbackSettingKey.ntpClawbackDays]: 'NTP',
  [MilestoneClawbackSettingKey.installClawbackDays]: 'Install',
};

export const MaterialClawbackSettingDisplayMap: Record<MaterialClawbackSettingKey, string> = {
  [MaterialClawbackSettingKey.partnerInstallSubmittal]: 'Install Submitted',
  [MaterialClawbackSettingKey.partnerInstallApproval]: 'Install Approved',
};

interface BaseOrganizationClawbackSettingsV2 {
  setBy?: UserWithDate;
}

export interface OrganizationMilestoneClawbackSettingsV2 extends BaseOrganizationClawbackSettingsV2 {
  /** NTP payment was made, but install has not occurred in these days */
  ntpClawbackDays?: number;
  /** Install payment was made, but activation has not occurred in these days */
  installClawbackDays?: number;
}

export interface OrganizationMaterialClawbackSettingsV2 extends BaseOrganizationClawbackSettingsV2 {
  /** We have paid a materials vendor (CED), but EPC has not met this install milestone within these days */
  partnerInstallSubmittal?: number;
  /** We have paid a materials vendor (CED), but EPC has not met this install milestone within these days */
  partnerInstallApproval?: number;
}

//Temporary
export interface OrganizationClawbackSettingsV2 {
  /** NTP payment was made, but install has not occurred in these days */
  ntpClawbackDays: number;
  /** Install payment was made, but activation has not occurred in these days */
  installClawbackDays: number;
  /** We have paid a materials vendor (CED), but EPC has not met this install milestone within these days */
  partnerInstallSubmittal: number;
  /** We have paid a materials vendor (CED), but EPC has not met this install milestone within these days */
  partnerInstallApproval: number;
}

export enum SafeHarborPricingStatus {
  active = 'active',
  inactive = 'inactive',
}

export interface OrganizationSafeHarborPricingSettingsV2 {
  sku: string;
  equipmentPrice: number;
  shippingPrice: number;
  status: SafeHarborPricingStatus;
  inactiveDate?: Date;
}

export interface OrganizationEquipmentSettingsV2 {
  allowedAllocations?: ApprovedVendorAllocation[];
  allowedEntryIds?: string[];
  setBy?: UserWithDate;
}

export enum MaintenancePeriodUnit {
  year = 'year',
  month = 'month',
  day = 'day',
}

export enum MaintenanceRateUnit {
  hourly = 'hourly',
  perSystem = 'perSystem',
}

export interface OrganizationMaintenanceSettingsV2Value {
  version: number;
  cadence: {
    times: number;
    per: {
      unit: MaintenancePeriodUnit;
      amount: number;
    };
  };
  maintenanceWindow: {
    unit: MaintenancePeriodUnit;
    amount: number;
  };
  maintenanceRate: {
    unit: MaintenanceRateUnit;
    amount: number;
  };
  serviceRate: {
    unit: MaintenanceRateUnit;
    amount: number;
  };
}

export type OrganizationMaintenanceSettingsV2 = OrganizationSettingsV2<OrganizationMaintenanceSettingsV2Value>;
